import { canvas, ctx2d, outline } from './pixel';

/**
 * Pixel-art icons for the HUD menu buttons (16×16, shown at 2×), hand-placed as letter grids
 * so each menu reads at a glance: a bust for the character, a pouch for the bag, a manual for
 * the arts, a taiji for neigong, and so on.
 */

/** Palette for the menu icons: one letter per colour, '.' is clear. */
const PAL: Record<string, string> = {
  k: '#2a1e1a',
  g: '#d9b46c',
  G: '#f6dc9a',
  d: '#8a6a38',
  w: '#ffffff',
  y: '#efe2c0',
  s: '#c8d0dc',
  x: '#7a8698',
  r: '#c8443b',
  R: '#ff8070',
  e: '#7a2020',
  j: '#74d4ad',
  J: '#c0f4dc',
  D: '#2f8a6a',
  b: '#9a6a3a',
  c: '#c89660',
  B: '#5a3a22',
  p: '#f0c8a0',
  P: '#c89070',
  h: '#2a2228',
  q: '#4f8bdc',
  Q: '#9cc8ff',
  n: '#26407a',
  m: '#b07ae8',
  M: '#dcc0ff',
  v: '#5a3a8a',
};

/** 16×16 pixel grids, one row per string. */
const GRIDS = {
  char: [
    '................',
    '.......hh.......',
    '......hhhh......',
    '.....hhhhhh.....',
    '.....hpppph.....',
    '.....pkppkp.....',
    '.....pppppp.....',
    '......pPPp......',
    '......wpPw......',
    '...qqqwwwwqqq...',
    '..qQQqqwwqqqqq..',
    '..qQqqqqwqqqqq..',
    '..qQqqgggggqnq..',
    '..qqqqqqwqqqnq..',
    '..nnnnnnnnnnnn..',
    '................',
  ],
  bag: [
    '................',
    '......b..b......',
    '.......bb.......',
    '......gddg......',
    '......BbbB......',
    '.....BcbbbB.....',
    '....BcbbbbbB....',
    '...BcbbbbbbbB...',
    '..BcbbbgGgbbbB..',
    '..BcbbgGGGgbbB..',
    '..BbbbgGddgbbB..',
    '..BbbbbgggbbbB..',
    '..BbbbbbbbbbbB..',
    '...BbbbbbbbbB...',
    '....BBBBBBBB....',
    '................',
  ],
  arts: [
    '................',
    '..ynqqqqqqqqqq..',
    '..ynqQQQyyyqqq..',
    '..knqQqqykyqqq..',
    '..ynqqqqyyyqqq..',
    '..ynqqqqykyqqq..',
    '..knqqqqyyyqqq..',
    '..ynqqqqykyqqq..',
    '..ynqqqqyyyqqq..',
    '..knqqqqqqqqqq..',
    '..ynqqqqqqqqqq..',
    '..ynqqqqqqqqqq..',
    '..knqqqqqqqqqq..',
    '..ynnnnnnnnnnn..',
    '..yyyyyyyyyyyy..',
    '................',
  ],
  neigong: [
    '................',
    '.......QQ.......',
    '....nnQQQQQQ....',
    '...nnQQnnQQQQ...',
    '..nnnQQnnQQQQQ..',
    '..nnnQQnnQQQQQ..',
    '..nnnQQQQQQQQQ..',
    '.nnnnnQQQQQQQQQ.',
    '.nnnnnnnnnQQQQQ.',
    '..nnnnnnnnnQQQ..',
    '..nnnnnQQnnQQQ..',
    '..nnnnnQQnnQQQ..',
    '...nnnnQQnnQQ...',
    '....nnnnnnQQ....',
    '.......nn.......',
    '................',
  ],
  school: [
    '................',
    '................',
    '.j............j.',
    '.jjjjjjjjjjjjjj.',
    '..DDDDDDDDDDDD..',
    '...rR.gGGg.rR...',
    '...rR.gddg.rR...',
    '..jjjjjjjjjjjj..',
    '..DDDDDDDDDDDD..',
    '...rR......rR...',
    '...rR......rR...',
    '...rR......rR...',
    '...rR......rR...',
    '...rR......rR...',
    '..xxxx....xxxx..',
    '................',
  ],
  skin: [
    '................',
    '.....mMwwMm.....',
    '...mmmmMwmmmm...',
    '..mMmmmmMwmmmm..',
    '.mMmmmmmmMmmmmv.',
    '.mMmmmmmmmMmmmv.',
    '.mMm.mmmmmmm.mv.',
    '.mmm.ggggggg.mv.',
    '.vvv.mmmmmmm.vv.',
    '.....mMmmmmv....',
    '.....mMmmmmmv...',
    '....mMmmmmmmv...',
    '....mMmmmmmmmv..',
    '...mMmmmmmmmmv..',
    '...vvvvvvvvvvv..',
    '................',
  ],
  mall: [
    '................',
    '.....JJJJJj.....',
    '....JJJJJjjj....',
    '...JJJJJjjjjj...',
    '..JJJJJjjjjjjj..',
    '..JJJJ....jjjj..',
    '..JJJj....jjjD..',
    '..JJjj....jjDD..',
    '..Jjjj....jDDD..',
    '..jjjjjjjjDDDD..',
    '...jjjjjjDDDD...',
    '....jjjjDDDD....',
    '.....jjDDDD.....',
    '.......gg.......',
    '......rRrr......',
    '......r.rr......',
  ],
  pass: [
    '................',
    '......gggg......',
    '.....gGGGGg.....',
    '....gGddddgd....',
    '....gdrrrrdd....',
    '....gdrwwrdd....',
    '....gdrrrrdd....',
    '....gdrwwrdd....',
    '....gdrrrrdd....',
    '....gdrwwrdd....',
    '....gdrrrrdd....',
    '....gddddddd....',
    '.....dddddd.....',
    '.......rr.......',
    '......rRrr......',
    '......r..r......',
  ],
  ladder: [
    '..G..........G..',
    '.GwG.Bb..Bb.GwG.',
    '.....BbccBb.....',
    '.....BbBBBb.....',
    '.....Bb..Bb.....',
    '.....BbccBb.....',
    '.....BbBBBb.....',
    '.....Bb..Bb.....',
    '.....BbccBb.....',
    '.....BbBBBb.....',
    '.....Bb..Bb.....',
    '.....BbccBb.....',
    '.....BbBBBb.....',
    '..R..Bb..Bb..R..',
    '.rRr.Bb..Bb.rRr.',
    '.rer........rer.',
  ],
  arena: [
    '................',
    '...GGGGGGGGGG...',
    '.ggGgggggggggdd.',
    '.g.gGggggggggd.d',
    '.g.gGggggggggd.d',
    '..ggGggggggggdd.',
    '....gGgggggggd..',
    '.....gGgggggd...',
    '......ggggdd....',
    '.......ggd......',
    '.......ggd......',
    '......gggdd.....',
    '.....rrrrrrr....',
    '....rRRRRRRRe...',
    '....eeeeeeeee...',
    '................',
  ],
  mount: [
    '................',
    '........B.B.....',
    '.......BbBbh....',
    '......Bbbbbhh...',
    '.....Bbkbbbhhh..',
    '....Bbbbbbbbhh..',
    '...Bcbbbbbbbbhh.',
    '..Bcbbb.Bbbbbhh.',
    '.Bcbbb...Bbbbbhh',
    '.ccbb....Bbbbbbh',
    '.kcb.....Bbbbbbb',
    '.........Bbbbbbb',
    '.........gGgggbb',
    '.........Bbbbbbb',
    '.........Bbbbbbb',
    '................',
  ],
  help: [
    '................',
    '...cbbbbbbbbbB..',
    '...BBBBBBBBBBB..',
    '....yyyyyyyyy...',
    '....yyykkkyyy...',
    '....yykyyykyy...',
    '....yyyyyykyy...',
    '....yyyyykyyy...',
    '....yyyykyyyy...',
    '....yyyykyyyy...',
    '....yyyyyyyyy...',
    '....yyyykyyyy...',
    '....yyyyyyyyy...',
    '...cbbbbbbbbbB..',
    '...BBBBBBBBBBB..',
    '................',
  ],
  // A folded map: three paper panels, a river, a path and a red mark.
  map: [
    '................',
    '................',
    '..yyyyPyyyyPyy..',
    '..yqqyPyyyyPyy..',
    '..yyqqPyyyyPyy..',
    '..yyyqqyyryPyy..',
    '..yyyyqqrrrPyy..',
    '..yyyyPqqryPyy..',
    '..yyyyPyqqyPyy..',
    '..ycyyPyyqqPyy..',
    '..yycyPyyyqqyy..',
    '..yyycPcyyyqqy..',
    '..yyyyccyyyPqq..',
    '..yyyyPyyyyPyy..',
    '................',
    '................',
  ],
  settings: [
    '................',
    '.......ss.......',
    '...s...ss...s...',
    '..ssssssssssxx..',
    '...ssxxssxxxx...',
    '...sxsssssxsx...',
    '...sxs....xsx...',
    '.sssss....xxxxx.',
    '.sssss....xxxxx.',
    '...sxs....xsx...',
    '...sxxxxxxxsx...',
    '...sxssxxssxx...',
    '..sxxxxxxxxxxx..',
    '...x...xx...x...',
    '.......xx.......',
    '................',
  ],
  soundOn: [
    '................',
    '................',
    '........s...Q...',
    '.......ss...QQ..',
    '......sss....Q..',
    '..sssssss..Q..Q.',
    '..sxxxsss..QQ.Q.',
    '..sxxxsss...Q.Q.',
    '..sxxxsss...Q.Q.',
    '..sxxxsss..QQ.Q.',
    '..sssssss..Q..Q.',
    '......sss....Q..',
    '.......ss...QQ..',
    '........s...Q...',
    '................',
    '................',
  ],
  soundOff: [
    '................',
    '................',
    '........s.......',
    '.......ss.......',
    '......sss.......',
    '..sssssss.r...r.',
    '..sxxxsss..r.r..',
    '..sxxxsss...r...',
    '..sxxxsss..r.r..',
    '..sxxxsss.r...r.',
    '..sssssss.......',
    '......sss.......',
    '.......ss.......',
    '........s.......',
    '................',
    '................',
  ],
  pk: [
    '................',
    '.............ww.',
    '............wsx.',
    '...........wsx..',
    '..........wsx...',
    '.........wsx....',
    '........wsx.....',
    '...g...wsx......',
    '...gg.wsx.......',
    '....ggsx........',
    '....Bgg.....R...',
    '...BB.gg...rRr..',
    '..BB...g...rrr..',
    '.gB.....R...e...',
    '.g.....rRr......',
    '........e.......',
  ],
  auto: [
    '................',
    '................',
    '.....JJJJJJ.....',
    '...JJJJJJJJJj...',
    '...JJJ....Jj..JJ',
    '..JJJ.......JJJ.',
    '..JJ......JJJJJ.',
    '..jJ.......JJJJ.',
    '.jjjj.......jJ..',
    '.jjjjj......jj..',
    '.jjj.......jjj..',
    'jj..jj....jjj...',
    '...jjjjjjjjjj...',
    '.....jjjjjj.....',
    '................',
    '................',
  ],
} satisfies Record<string, string[]>;

export type MenuIcon = keyof typeof GRIDS;

const cache = new Map<MenuIcon, HTMLCanvasElement>();

function draw(name: MenuIcon): HTMLCanvasElement {
  const hit = cache.get(name);
  if (hit) return hit;
  const rows = GRIDS[name];
  const c = canvas(rows[0]!.length, rows.length);
  const g = ctx2d(c);
  rows.forEach((row, y) => {
    for (let x = 0; x < row.length; x++) {
      const col = PAL[row[x]!];
      if (!col) continue;
      g.fillStyle = col;
      g.fillRect(x, y, 1, 1);
    }
  });
  outline(c, '#0a080c');
  cache.set(name, c);
  return c;
}

/** A fresh element showing a menu icon. */
export function menuIcon(name: MenuIcon): HTMLCanvasElement {
  const src = draw(name);
  const c = canvas(src.width, src.height);
  c.className = 'micon';
  ctx2d(c).drawImage(src, 0, 0);
  return c;
}

/** Fills every `<i data-icon="…">` under `root` with its pixel icon. */
export function mountMenuIcons(root: ParentNode = document): void {
  for (const el of root.querySelectorAll<HTMLElement>('i[data-icon]'))
    setMenuIcon(el, el.dataset.icon as MenuIcon);
}

/** Swaps the icon inside an `<i>` holder (e.g. sound on/off). */
export function setMenuIcon(el: HTMLElement, name: MenuIcon): void {
  if (el.dataset.shown === name) return;
  el.dataset.shown = name;
  el.replaceChildren(menuIcon(name));
}
